import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from 'react-modal';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import { FiEdit, FiTrash } from 'react-icons/fi';
import { useRef } from 'react';
import { useAcademicYear } from '../contexts/AcademicYearContext';
import { useAuth } from '../contexts/AuthContext';
import ConditionalButton from '../components/common/ConditionalButton';
import ConditionalActions from '../components/common/ConditionalActions';
import FileUpload from '../components/FileUpload';

const emptyEvaluation = {
  ouverture_classe_id: '',
  matiere_id: '',
  periode_id: '',
  type_evaluation_id: '',
  date_evaluation: '',
  pj_devoirs: ''
};

const EvaluationsPage = () => {
  const { user } = useAuth();
  const [evaluations, setEvaluations] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyEvaluation);
  const [matieres, setMatieres] = useState([]);
  const [classeMatieres, setClasseMatieres] = useState([]);
  const [periodes, setPeriodes] = useState([]);
  const [types, setTypes] = useState([]);
  const [ouvertures, setOuvertures] = useState([]);
  const { selectedYear } = useAcademicYear();
  // Tri, pagination
  const [sortBy, setSortBy] = useState('date_evaluation');
  const [sortOrder, setSortOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  // Formatage date
  const formatDateFr = (dateStr) => {
    if (!dateStr) return '';
    const datePart = dateStr.split(' ')[0].split('T')[0];
    const [y, m, d] = datePart.split('-');
    return `${d}/${m}/${y}`;
  };
  // États pour la recherche/filtrage
  const [searchClasse, setSearchClasse] = useState('');
  const [searchMatiere, setSearchMatiere] = useState('');
  const [searchPeriode, setSearchPeriode] = useState('');
  const [searchType, setSearchType] = useState('');
  const [searchDateDebut, setSearchDateDebut] = useState('');
  const [searchDateFin, setSearchDateFin] = useState('');
  const [pjFiles, setPjFiles] = useState([]);
  // Pour la saisie des notes
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesEval, setNotesEval] = useState(null); // {eval, ouverture, matiere, annee}
  const [elevesNotes, setElevesNotes] = useState([]); // [{eleve, note, ...}]
  const [notesLocked, setNotesLocked] = useState(false);
  const notesInputsRefs = useRef([]);

  useEffect(() => {
    fetchEvaluations();
    fetchMatieres();
    fetchPeriodes();
    fetchTypes();
    api.get('/classe-matieres').then(res => setClasseMatieres(res.data.data || []));
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchOuvertures(selectedYear.id);
    } else {
      setOuvertures([]);
    }
  }, [selectedYear]);

  const fetchEvaluations = async () => {
    const res = await api.get('/evaluations');
    setEvaluations(res.data.data || []);
  };
  const fetchMatieres = async () => {
    const res = await api.get('/matieres');
    setMatieres(res.data.data || []);
  };
  const fetchPeriodes = async () => {
    const res = await api.get('/periodes');
    setPeriodes(res.data.data || []);
  };
  const fetchTypes = async () => {
    const res = await api.get('/type-evaluations');
    setTypes(res.data.data || []);
  };

  const fetchOuvertures = async (anneeId) => {
    const res = await api.get('/ouvertures-classes');
    // Filtrer par année académique sélectionnée
    setOuvertures((res.data.data || []).filter(o => o.annee_scolaire_id === anneeId));
  };

  const handleAdd = () => {
    setEditing(null);
    setForm(emptyEvaluation);
    setPjFiles([]);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      ouverture_classe_id: item.ouverture_classe_id || '',
      matiere_id: item.matiere_id || '',
      periode_id: item.periode_id || '',
      type_evaluation_id: item.type_evaluation_id || '',
      date_evaluation: item.date_evaluation || '',
      pj_devoirs: item.pj_devoirs || ''
    });
    setPjFiles([]); // Optionnel : charger les fichiers existants si besoin
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Supprimer cette évaluation ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    });
    if (result.isConfirmed) {
      await api.delete(`/evaluations/${id}`);
      fetchEvaluations();
      Swal.fire('Supprimé !', 'L\'évaluation a été supprimée.', 'success');
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditing(null);
    setForm(emptyEvaluation);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.ouverture_classe_id || !form.matiere_id || !form.periode_id || !form.type_evaluation_id || !form.date_evaluation) {
      await Swal.fire({ icon: 'error', title: 'Erreur', text: 'Tous les champs obligatoires doivent être remplis.' });
      return;
    }
    // Préparer l'envoi avec fichiers
    const formData = new FormData();
    Object.entries(form).forEach(([key, value]) => formData.append(key, value));
    pjFiles.forEach((file, idx) => formData.append('pj_devoirs[]', file));
    if (editing) {
      await api.put(`/evaluations/${editing.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    } else {
      await api.post('/evaluations', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    }
    fetchEvaluations();
    setShowModal(false);
  };

  // Filtrage par année académique sélectionnée + recherche
  const ouvertureIds = ouvertures.map(o => o.ouverture_classe_id || o.id);
  let filteredEvals = evaluations.filter(ev => {
    if (!ouvertureIds.includes(ev.ouverture_classe_id)) return false;
    if (searchClasse && String(ev.ouverture_classe_id) !== searchClasse) return false;
    if (searchMatiere && String(ev.matiere_id) !== searchMatiere) return false;
    if (searchPeriode && String(ev.periode_id) !== searchPeriode) return false;
    if (searchType && String(ev.type_evaluation_id) !== searchType) return false;
    if (searchDateDebut && ev.date_evaluation < searchDateDebut) return false;
    if (searchDateFin && ev.date_evaluation > searchDateFin) return false;
    return true;
  });
  // Tri dynamique
  filteredEvals = filteredEvals.sort((a, b) => {
    let va, vb;
    if (sortBy === 'matiere') {
      va = matieres.find(m => m.id === a.matiere_id)?.lib_matiere || '';
      vb = matieres.find(m => m.id === b.matiere_id)?.lib_matiere || '';
    } else if (sortBy === 'periode') {
      va = periodes.find(p => p.id === a.periode_id)?.periode || '';
      vb = periodes.find(p => p.id === b.periode_id)?.periode || '';
    } else if (sortBy === 'ouverture_classe_id') {
      va = a.ouverture_classe_id;
      vb = b.ouverture_classe_id;
    } else {
      va = a[sortBy];
      vb = b[sortBy];
    }
    if (va < vb) return sortOrder === 'asc' ? -1 : 1;
    if (va > vb) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
  // Pagination
  const totalPages = Math.ceil(filteredEvals.length / pageSize);
  const paginatedEvals = filteredEvals.slice((page - 1) * pageSize, page * pageSize);
  // Fonction de tri sur en-tête
  const handleSort = (col) => {
    if (sortBy === col) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(col);
      setSortOrder('asc');
    }
  };

  // Fonction d'appréciation automatique
  function getAppreciation(note) {
    const n = parseFloat(note);
    if (isNaN(n)) return '';
    if (n >= 16) return 'Excellent';
    if (n >= 14) return 'Très bien';
    if (n >= 12) return 'Bien';
    if (n >= 10) return 'Passable';
    if (n >= 8) return 'Insuffisant';
    if (n >= 6) return 'Faible';
    return 'Très faible';
  }
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Évaluations</h1>
        <ConditionalButton
          entity="evaluations"
          permission="create"
          className="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700"
          onClick={handleAdd}
        >
          Nouvelle évaluation
        </ConditionalButton>
      </div>
      {/* Formulaire de recherche/filtrage */}
      <div className="bg-white shadow rounded-lg overflow-hidden mb-4 p-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs font-medium mb-1">Classe</label>
            <select value={searchClasse} onChange={e => setSearchClasse(e.target.value)} className="border rounded px-3 py-2">
              <option value="">Toutes</option>
              {ouvertures.map(o => (
                <option key={o.ouverture_classe_id || o.id} value={o.ouverture_classe_id || o.id}>{o.classe?.classe || o.classe_id}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Matière</label>
            <select value={searchMatiere} onChange={e => setSearchMatiere(e.target.value)} className="border rounded px-3 py-2">
              <option value="">Toutes</option>
              {matieres.map(m => (
                <option key={m.id} value={m.id}>{m.lib_matiere}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Période</label>
            <select value={searchPeriode} onChange={e => setSearchPeriode(e.target.value)} className="border rounded px-3 py-2">
              <option value="">Toutes</option>
              {periodes.map(p => (
                <option key={p.id} value={p.id}>{p.periode}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Type</label>
            <select value={searchType} onChange={e => setSearchType(e.target.value)} className="border rounded px-3 py-2">
              <option value="">Tous</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>{t.type_evaluation}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Date début</label>
            <input type="date" value={searchDateDebut} onChange={e => setSearchDateDebut(e.target.value)} className="border rounded px-3 py-2" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1">Date fin</label>
            <input type="date" value={searchDateFin} onChange={e => setSearchDateFin(e.target.value)} className="border rounded px-3 py-2" />
          </div>
          <div>
            <button type="button" className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300" onClick={() => {
              setSearchClasse(''); setSearchMatiere(''); setSearchPeriode(''); setSearchType(''); setSearchDateDebut(''); setSearchDateFin('');
            }}>Réinitialiser</button>
          </div>
        </div>
      </div>
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-1 cursor-pointer" onClick={() => handleSort('ouverture_classe_id')}>Classe</th>
              <th className="px-6 py-1 cursor-pointer" onClick={() => handleSort('matiere')}>Matière</th>
              <th className="px-6 py-1 cursor-pointer" onClick={() => handleSort('periode')}>Période</th>
              <th className="px-6 py-1">Type</th>
              <th className="px-6 py-1 cursor-pointer" onClick={() => handleSort('date_evaluation')}>Date</th>
              <th className="px-6 py-1 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {paginatedEvals.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-gray-500">Aucune évaluation</td>
              </tr>
            ) : (
              paginatedEvals.map((item) => (
                <tr key={item.id}>
                  <td className="px-6 py-1">{ouvertures.find(o => o.ouverture_classe_id === item.ouverture_classe_id)?.classe?.classe || item.ouverture_classe_id}</td>
                  <td className="px-6 py-1">{matieres.find(m => m.id === item.matiere_id)?.lib_matiere || item.matiere_id}</td>
                  <td className="px-6 py-1">{periodes.find(p => p.id === item.periode_id)?.periode || item.periode_id}</td>
                  <td className="px-6 py-1">{types.find(t => t.id === item.type_evaluation_id)?.type_evaluation || item.type_evaluation_id}</td>
                  <td className="px-6 py-1">{formatDateFr(item.date_evaluation)}</td>
                  <td className="px-6 py-1 text-right">
                    <button
                      className="p-2 text-blue-600 hover:text-blue-900 mr-2 rounded-full"
                      onClick={async () => {
                        // Charger la liste des élèves de la classe
                        const ouverture = ouvertures.find(o => o.ouverture_classe_id === item.ouverture_classe_id);
                        const matiere = matieres.find(m => m.id === item.matiere_id);
                        const res = await api.get(`/inscriptions?ouverture_classe_id=${item.ouverture_classe_id}`);
                        const eleves = res.data.data || [];
                        // Charger les notes existantes
                        const notesRes = await api.get(`/details-evaluation?eval_id=${item.id}`);
                        const details = notesRes.data.data || [];
                        setNotesEval({ eval: item, ouverture, matiere, annee: selectedYear });
                        console.log('eleves:', eleves);
                        console.log('details:', details);

                        setElevesNotes(eleves.map((el, idx) => {
  const d = details.find(x => String(x.eleve_id) === String(el.eleve_id)) || { note: '', non_applicable: 'N', appreciation: '', id: null };
  return {
    ...el,
    note: d.note
      ? (typeof d.note === 'string' ? d.note.replace(',', '.') : d.note)
      : '',
    non_applicable: d.non_applicable ?? 'N',
    appreciation: d.appreciation,
    detail_id: d.id,
    initial_note: d.note
      ? (typeof d.note === 'string' ? d.note.replace(',', '.') : d.note)
      : '',
    initial_non_applicable: d.non_applicable ?? 'N',
    initial_appreciation: d.appreciation,
    refIdx: idx
  };
}));
                        
                        setNotesLocked(details.length > 0); // Verrouillé si des notes existent
                        setTimeout(() => { if (notesInputsRefs.current[0]) notesInputsRefs.current[0].focus(); }, 100);
                        setShowNotesModal(true);
                      }}
                      title="Saisir les notes"
                    >
                      📝
                    </button>
                    <ConditionalActions
                      entity="evaluations"
                      onEdit={() => handleEdit(item)}
                      onDelete={() => handleDelete(item.id)}
                      editIcon={FiEdit}
                      deleteIcon={FiTrash}
                      className="flex space-x-2"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {/* Pagination compacte et professionnelle */}
        <div className="flex justify-center items-center gap-1 mt-4 flex-wrap pb-4">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm"
          >Premier</button>
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm"
          >Précédent</button>
          {/* Numéros de page dynamiques avec ellipses */}
          {(() => {
            const pages = [];
            if (totalPages <= 7) {
              for (let p = 1; p <= totalPages; p++) pages.push(p);
            } else {
              pages.push(1, 2);
              if (page > 4) pages.push('...');
              for (let p = Math.max(3, page - 2); p <= Math.min(totalPages - 2, page + 2); p++) {
                if (p > 2 && p < totalPages - 1) pages.push(p);
              }
              if (page < totalPages - 3) pages.push('...');
              pages.push(totalPages - 1, totalPages);
            }
            return pages.map((p, idx) =>
              p === '...'
                ? <span key={`ellipsis-${idx}`} className="px-2 text-gray-400">...</span>
                : <button
                    key={`page-${p}`}
                    onClick={() => setPage(p)}
                    className={`px-2 py-1 rounded mx-0.5 text-sm ${p === page ? 'bg-primary-600 text-white' : 'bg-gray-100 hover:bg-primary-100 text-gray-700'}`}
                    disabled={p === page}
                  >{p}</button>
            );
          })()}
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm"
          >Suivant</button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            className="px-2 py-1 rounded bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-sm"
          >Dernier</button>
        </div>
      </div>
      {/* Modal de saisie des notes */}
      <Modal
        isOpen={showNotesModal}
        onRequestClose={() => setShowNotesModal(false)}
        shouldCloseOnOverlayClick={false}
        contentLabel="Saisie des notes"
        ariaHideApp={false}
        style={{
          content: {
            maxWidth: '800px',
            width: '100%',
            height: '600px',
            margin: 'auto',
            borderRadius: 12,
            padding: 0,
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            transform: 'translate(-50%, -50%)',
            overflow: 'auto',
          }
        }}
      >
        <div className="p-6 relative">
          <div className="sticky top-0 z-30 bg-white pb-2 border-b flex flex-col">
            <div className="flex items-start justify-between">
              <h2 className="text-lg font-semibold mb-1">Saisie des notes</h2>
              <button
                type="button"
                onClick={() => setShowNotesModal(false)}
                className="ml-4 mt-1 text-gray-400 hover:text-gray-700 text-2xl font-bold focus:outline-none sticky top-0 z-40 bg-white"
                title="Fermer"
                style={{height: 36, minWidth: 36, lineHeight: '36px', borderRadius: '50%'}}
              >
                ×
              </button>
            </div>
            {notesEval && (
              <div className="text-sm text-gray-700">
                <div><b>Matière :</b> {notesEval.matiere?.lib_matiere || notesEval.eval.matiere_id}</div>
                <div><b>Classe :</b> {notesEval.ouverture?.classe?.classe || notesEval.eval.ouverture_classe_id}</div>
                <div><b>Année :</b> {notesEval.annee?.libelle || notesEval.annee?.annee_scolaire || ''}</div>
              </div>
            )}
          </div>
          <div className="flex justify-end mb-2">
            <button
              type="button"
              className={`px-4 py-2 rounded ${notesLocked ? 'bg-gray-400 text-white' : 'bg-primary-600 text-white'} hover:bg-primary-700`}
              onClick={() => setNotesLocked(l => !l)}
            >
              {notesLocked ? 'Déverrouiller' : 'Verrouiller'}
            </button>
          </div>
          <form onSubmit={async (e) => {
              e.preventDefault();
              const promises = elevesNotes.map(el => {
                const changed =
                  String(el.note) !== String(el.initial_note) ||
                  String(el.non_applicable) !== String(el.initial_non_applicable) ||
                  String(el.appreciation) !== String(el.initial_appreciation);
                if (!changed) return null;
                // Conversion du point en virgule pour compatibilité backend
                const notePourBackend = (typeof el.note === 'string')
                  ? el.note.replace('.', ',')
                  : (el.note !== undefined && el.note !== null
                      ? String(el.note).replace('.', ',')
                      : '');
                if (el.detail_id) {
                  return api.put(`/details-evaluation/${el.detail_id}`, {
                    eval_id: notesEval.eval.id,
                    eleve_id: el.eleve_id,
                    note: notePourBackend || 0,
                    non_applicable: el.non_applicable || 'N',
                    appreciation: el.appreciation || ''
                  });
                } else {
                  return api.post('/details-evaluation', {
                    eval_id: notesEval.eval.id,
                    eleve_id: el.eleve_id,
                    note: notePourBackend || 0,
                    non_applicable: el.non_applicable || 'N',
                    appreciation: el.appreciation || ''
                  });
                }
              }).filter(Boolean);
              await Promise.all(promises);
              setShowNotesModal(false);
            }}
          >
            <table className="min-w-full bg-white border mb-4">
              <thead className="sticky top-[100px] z-20 bg-white border-b">
                <tr>
                  <th className="border px-2 py-1">Élève</th>
                  <th className="border px-2 py-1">Note</th>
                  <th className="border px-2 py-1">Non applicable</th>
                  <th className="border px-2 py-1">Appréciation</th>
                </tr>
              </thead>
              <tbody>
                {elevesNotes.map((el, idx) => (
                  <tr key={el.id}>
                    <td className="border px-2 py-1">{el.nom} {el.prenom}</td>
                    <td className="border px-2 py-1">
                      <input
                        type="number"
                        value={el.note ? String(el.note).replace(',', '.') : ''}
                        min="0"
                        max="20"
                        step="0.01"
                        ref={ref => notesInputsRefs.current[idx] = ref}
                        onChange={e => {
                          let note = e.target.value;
                          if (parseFloat(note.replace(',', '.')) > 20) note = '20';
                          setElevesNotes(prev => prev.map((x, i) => i === idx ? { ...x, note, appreciation: getAppreciation(note.replace(',', '.')) } : x));
                        }}
                        className="w-20 border rounded px-2 py-1"
                        disabled={el.non_applicable === 'Y' || notesLocked}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === 'Tab') {
                            e.preventDefault();
                            const next = notesInputsRefs.current[idx + 1];
                            if (next) next.focus();
                          }
                        }}
                      />
                    </td>
                    <td className="border px-2 py-1 text-center">
                      <input
                        type="checkbox"
                        checked={el.non_applicable === 'Y'}
                        onChange={e => setElevesNotes(prev => prev.map((x, i) => i === idx ? { ...x, non_applicable: e.target.checked ? 'Y' : 'N' } : x))}
                        disabled={notesLocked}
                      />
                    </td>
                    <td className="border px-2 py-1">
                      <input
                        type="text"
                        value={getAppreciation(el.note)}
                        className="w-full border rounded px-2 py-1 bg-gray-100 text-gray-700"
                        readOnly
                        tabIndex={-1}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowNotesModal(false)} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">Annuler</button>
              <button type="submit" disabled={notesLocked} className={`px-4 py-2 rounded ${notesLocked ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-primary-600 text-white hover:bg-primary-700'}`}>Enregistrer</button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={showModal}
        onRequestClose={handleModalClose}
        contentLabel="Évaluation"
        ariaHideApp={false}
        style={{
        content: {
          maxWidth: '500px',
          width: '100%',
          margin: 'auto',
          borderRadius: 12,
          overflow: 'hidden',
          padding: 0,
          top: '50%',
          left: '50%',
          right: 'auto',
          bottom: 'auto',
          transform: 'translate(-50%, -50%)',
        }
      }}
      >
        <form onSubmit={handleSubmit} className="p-6">
          <h2 className="text-lg font-semibold mb-4">{editing ? 'Modifier' : 'Nouvelle'} évaluation</h2>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Classe ouverte *</label>
            <select
              name="ouverture_classe_id"
              value={form.ouverture_classe_id}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Sélectionner...</option>
              {ouvertures.map((o) => (
                <option key={o.ouverture_classe_id || o.id} value={o.ouverture_classe_id || o.id}>
                  {o.classe?.classe || o.classe_id}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Matière *</label>
            <select
              name="matiere_id"
              value={form.matiere_id}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Sélectionner...</option>
              {classeMatieres
                .filter(cm => String(cm.ouverture_classe_id) === String(form.ouverture_classe_id))
                .map(cm => {
                  const mat = matieres.find(m => m.id === cm.matiere_id);
                  return mat ? <option key={mat.id} value={mat.id}>{mat.lib_matiere}</option> : null;
                })}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Période *</label>
            <select
              name="periode_id"
              value={form.periode_id}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Sélectionner...</option>
              {periodes.map((p) => (
                <option key={p.id} value={p.id}>{p.periode}</option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Type *</label>
            <select
              name="type_evaluation_id"
              value={form.type_evaluation_id}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            >
              <option value="">Sélectionner...</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>{t.type_evaluation}</option>
              ))}
            </select>
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Date *</label>
            <input
              type="date"
              name="date_evaluation"
              value={form.date_evaluation}
              onChange={handleChange}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div className="mb-4">
            <FileUpload files={pjFiles} setFiles={setPjFiles} />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={handleModalClose} className="px-4 py-2 rounded bg-gray-200 hover:bg-gray-300">Annuler</button>
            <ConditionalButton
              entity="evaluations"
              permission={editing ? "update" : "create"}
              type="submit"
              className="px-4 py-2 rounded bg-primary-600 text-white hover:bg-primary-700"
            >
              {editing ? 'Enregistrer' : 'Créer'}
            </ConditionalButton>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default EvaluationsPage;
